import React from "react";
import Head from 'next/head';
import type { ImageProps, StaticImageData } from 'next/image';
import { buildImageSrc } from "./buildImageSrc";
import { extname } from "./extname";
import { fallbackBlurDataURL, ImageLoaderType, universalFallbackFormat } from "./OptimizeImage";
import { fetchPriorityProps } from "./fetchPriority";
import { buildSrcSet, capCandidates, imageCandidates, ImageCandidate, sourceDimensionsFromSrc } from "./imageCandidates";

// O `StaticImport` do Next não é exportado por nenhum caminho público; vem
// daqui em vez de `next/dist/shared/lib/get-img-props`, que é interno.
type StaticImport = Exclude<ImageProps['src'], string>;

interface CustomImageProps {
  className?: string;
  breakpoint?: number;
  customBreakPoint?: number;
  desktopImage: string | StaticImageData;
  mobileImage: string | StaticImageData;
  containerWidth?: string | number | '100vw' | '100%';
  height?: number | string | 'auto';
  /**
   * `minWidth`/`maxWidth`/`minHeight`/`maxHeight`: @deprecated para o srcset.
   * Até a 3.x escolhiam as larguras do `<source>` de desktop (junto com
   * `breakpoint`); agora as larguras vêm da config de imagens do projeto
   * (`images.deviceSizes`), filtradas pelo `sizes`. Continuam aceitas.
   */
  minWidth?: number;
  maxWidth?: number;
  minHeight?: number;
  maxHeight?: number;
  alt?: string;
  sizes?: string;
  loader: ImageLoaderType;
  isProduction?: boolean;
  basePath?: string;
  imgproxyPort: string;
  /**
   * @deprecated AMP saiu na 4.0.0 (o Next 16 removeu o suporte). Aceita só
   * `false`, para não quebrar quem já passa `isAmp={false}`.
   */
  isAmp?: false;
  /** Qualidade pedida ao imgproxy. Default: 80. */
  quality?: number;
  lockHeightAtMin?: boolean;
  style?: React.CSSProperties;
  imgStyle?: React.CSSProperties;
  /**
   * `'blur'` pinta o LQIP de `blurDataURL` atrás do `<picture>` até a imagem
   * real carregar. `'empty'` (default) mantém o comportamento anterior: a caixa
   * reserva o espaço (sem layout shift) mas fica visualmente vazia.
   */
  placeholder?: 'blur' | 'empty';
  /**
   * Data URI do LQIP, o mesmo campo `blurDataURL` que o `changeDataByLocal`
   * anexa a cada imagem do CMS. Sem ele, `placeholder='blur'` cai no cinza
   * neutro embutido (`fallbackBlurDataURL`).
   *
   * É UM só para os dois breakpoints, de propósito: a 15px e com `blur(20px)`
   * por cima, o recorte de desktop e o de mobile são indistinguíveis, e um
   * borrão por media query exigiria injetar um `<style>` por instância.
   * Passe o da imagem de desktop.
   */
  blurDataURL?: string;
  /**
   * Imagem acima da dobra (candidata a LCP). Default: `false`.
   *
   * Com `true`, emite um `<link rel="preload">` por breakpoint com
   * `type="image/avif"` e `imagesrcset` igual, caractere por caractere, ao do
   * `<source>` avif correspondente — é isso que faz o navegador reaproveitar o
   * download em vez de baixar duas vezes —, e o `<img>` sai com
   * `fetchpriority="high"`. Com `false`, não há preload e o `<img>` sai com
   * `loading="lazy"`.
   *
   * Até a 3.4.0 o componente emitia dois preloads em toda instância, sem
   * condição, apontando para a imagem ORIGINAL (sem imgproxy, sem formato): o
   * navegador moderno baixava esse arquivo inteiro e nunca o exibia, porque usa
   * o avif do `<source>`. Ver TD-3 em docs/tech-debts.md.
   */
  priority?: boolean;
}

type AdaptiveImageProps = Omit<ImageProps, 'width' | 'height' | 'src' | 'alt' | 'placeholder' | 'blurDataURL'> & CustomImageProps & {
  width?: number | `${number}` | '100vw' | '100%';
  height?: number | `${number}` | 'auto';
  src?: string;
  alt?: string;
};

export const normalizeSrc = (src: string | StaticImport): string => {
  if (typeof src === 'string') return src;
  if ('default' in src) return src.default.src;
  return src.src;
};

/** Largura de referência para o `getImageProps`: com `sizes`, ele não a usa. */
const REFERENCE_WIDTH = 1920;

/**
 * Imagem de fundo com recorte por breakpoint: um `<source>` de desktop e um de
 * mobile por formato, e um `<img>` de fallback.
 *
 * As larguras do srcset vêm do `getImageProps` do Next, com a config de imagens
 * do projeto e o `sizes` do componente (default `100vw`); cada largura é um
 * arquivo de verdade, na proporção da origem — o recorte fica com o CSS
 * (`imgStyle.objectFit`). Até a 3.x as larguras eram montadas à mão
 * (`minWidth`/`breakpoint`/`maxWidth` no desktop, 320/480/640 no mobile), todas
 * apontando para o mesmo arquivo em tamanho original, e o `<img>` de fallback
 * carregava a imagem original. Ver TD-4 em docs/tech-debts.md.
 */
const AdaptiveImage: React.FC<AdaptiveImageProps> = ({
  className,
  alt = 'Background Image',
  sizes = '100vw',
  breakpoint = 768,
  desktopImage,
  mobileImage,
  width,
  containerWidth,
  height = 'auto',
  loader,
  isProduction,
  basePath,
  imgproxyPort,
  quality,
  style = {},
  imgStyle = {},
  placeholder = 'empty',
  blurDataURL,
  priority = false,
}) => {
  const [blurComplete, setBlurComplete] = React.useState(false);
  // Uma imagem já em cache termina de carregar ANTES da hidratação, e nesse
  // caso o `onLoad` do React nunca dispara — o borrão ficaria para sempre.
  // O ref checa `complete` no momento em que o nó entra no DOM.
  const imgRef = React.useCallback((img: HTMLImageElement | null) => {
    if (img?.complete) setBlurComplete(true);
  }, []);

  const showBlur = placeholder === 'blur' && !blurComplete;
  const resolvedBlurDataURL = blurDataURL || fallbackBlurDataURL;

  const srcImageDesktop = normalizeSrc(desktopImage);
  const srcImageMobile = normalizeSrc(mobileImage);
  const allCandidates = imageCandidates({ width: REFERENCE_WIDTH, height: REFERENCE_WIDTH, sizes });
  // Larguras limitadas à origem de CADA imagem (desktop e mobile podem diferir).
  // A dimensão vem do nome do arquivo (`…-954x357.png`) ou do import estático —
  // igual no build e no navegador.
  const candidatesFor = (image: string | StaticImageData, src: string): ImageCandidate[] => {
    const source = typeof image === 'string' ? sourceDimensionsFromSrc(src) : image;
    return capCandidates(allCandidates, { displayWidth: REFERENCE_WIDTH, maxWidth: source?.width });
  };
  const desktopCandidates = candidatesFor(desktopImage, srcImageDesktop);
  const mobileCandidates = candidatesFor(mobileImage, srcImageMobile);

  const urlFor = (src: string, format: 'avif' | 'webp' | 'jpg' | 'png') => (w: number) =>
    loader({ src, width: w, quality, format, isProduction, basePath, imgproxyPort });

  const srcSetFor = (src: string, format: 'avif' | 'webp') => {
    try {
      return buildSrcSet(src === srcImageDesktop ? desktopCandidates : mobileCandidates, urlFor(src, format));
    } catch (err) {
      // `error`: o srcset sai com a URL original, não a otimizada (TD-4).
      console.error(`AdaptiveImage: fallback for format ${format}`, err);
      return buildImageSrc(src);
    }
  };

  const desktopMedia = `(min-width: ${breakpoint}px)`;
  const mobileMedia = `(max-width: ${breakpoint - 1}px)`;

  // Gerados uma vez e usados no `<source>` e no preload: o `imagesrcset` do
  // preload só é reaproveitado se for o MESMO texto do srcset escolhido.
  const avifDesktop = srcSetFor(srcImageDesktop, 'avif');
  const avifMobile = srcSetFor(srcImageMobile, 'avif');
  const webpDesktop = srcSetFor(srcImageDesktop, 'webp');
  const webpMobile = srcSetFor(srcImageMobile, 'webp');

  // Fallback para o navegador sem avif nem webp: formato universal (jpg/png).
  const fallbackFormat = universalFallbackFormat(extname(srcImageDesktop).toLowerCase().replace('.', ''));
  let fallbackSrcSet: string | undefined;
  let fallbackSrc: string;
  try {
    fallbackSrcSet = buildSrcSet(desktopCandidates, urlFor(srcImageDesktop, fallbackFormat));
    fallbackSrc = urlFor(srcImageDesktop, fallbackFormat)(desktopCandidates[desktopCandidates.length - 1].width);
  } catch (err) {
    console.error(`AdaptiveImage: fallback for format ${fallbackFormat}`, err);
    fallbackSrcSet = undefined;
    fallbackSrc = buildImageSrc(srcImageDesktop);
  }

  const containerStyle: React.CSSProperties = {
    position: 'relative',
    width: containerWidth
      ? (typeof containerWidth === 'string' ? containerWidth : `${containerWidth}px`)
      : (typeof width === 'string' ? width : '100vw'),
    height: typeof height === 'string' ? height : `${height}px`,
    overflow: 'hidden',
  };

  return (
    <>
      {priority && (
        <Head>
          {/* `type` impede o navegador sem avif de baixar o arquivo errado; o
              `key` evita preload duplicado se a mesma imagem aparecer duas vezes. */}
          <link
            key={`wp-preload:${avifDesktop}`}
            rel='preload'
            as='image'
            type='image/avif'
            media={desktopMedia}
            imageSrcSet={avifDesktop}
            imageSizes={sizes}
            {...fetchPriorityProps('high')}
          />
          <link
            key={`wp-preload:${avifMobile}`}
            rel='preload'
            as='image'
            type='image/avif'
            media={mobileMedia}
            imageSrcSet={avifMobile}
            imageSizes={sizes}
            {...fetchPriorityProps('high')}
          />
        </Head>
      )}
      <div style={containerStyle} className={className}>
        {showBlur && (
          <div
            aria-hidden='true'
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `url("${resolvedBlurDataURL}")`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              // O blur sangra as bordas para dentro e deixaria uma moldura
              // translúcida; o scale empurra essa franja para fora do
              // `overflow: hidden` do container.
              filter: 'blur(20px)',
              transform: 'scale(1.05)',
            }}
          />
        )}
        <picture>
          {/* AVIF */}
          <source type='image/avif' media={desktopMedia} srcSet={avifDesktop} sizes={sizes} />
          <source type='image/avif' media={mobileMedia} srcSet={avifMobile} sizes={sizes} />

          {/* WEBP */}
          <source type='image/webp' media={desktopMedia} srcSet={webpDesktop} sizes={sizes} />
          <source type='image/webp' media={mobileMedia} srcSet={webpMobile} sizes={sizes} />

          {/* Fallback: formato universal, para quem não decodifica avif nem webp */}
          <img
            alt={alt}
            decoding='async'
            // No `<img>` de um `<picture>`, `loading` e `fetchpriority` valem
            // para o `<source>` escolhido.
            loading={priority ? undefined : 'lazy'}
            {...fetchPriorityProps(priority ? 'high' : undefined)}
            ref={imgRef}
            onLoad={() => setBlurComplete(true)}
            // Se a imagem real falhar, o borrão tem que sair mesmo assim —
            // senão o erro vira um placeholder permanente.
            onError={() => setBlurComplete(true)}
            style={{
              // O overlay do LQIP é posicionado e, sem isto, pintaria POR CIMA
              // do `<img>`, que é estático. Um elemento posicionado mais à
              // frente no DOM ganha — daí o `relative` aqui. Vem antes dos
              // estilos do consumidor para que ele possa sobrescrever.
              position: 'relative',
              ...style,
              ...imgStyle,
            }}
            sizes={sizes}
            srcSet={fallbackSrcSet}
            // `src` por último, como o `next/image` (Safari).
            src={fallbackSrc}
          />
        </picture>
      </div>
    </>
  );
};

AdaptiveImage.displayName = 'AdaptiveImage';

export default AdaptiveImage;
